import React, { useEffect, useMemo, useState } from "react";
import {
  Box, TextField, Button, Typography, CircularProgress, Paper, Grid, Alert,
  MenuItem
} from "@mui/material";
import { useAuthContext } from "../context/AuthContext";
import { getSelectedCompanyIds, resolveSnapshotProjectId } from "../utils/primaryProjectId";
import { collectAwarenessDescendantIds } from "../utils/awarenessGroupTree";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));

/**
 * Props:
 * - data: { id?, nome?, ordem?, id_pai?, excluido? }
 * - onSubmit(payload)
 * - onClose()
 * - isLoading
 * - (Opcional) groups para seleção de pai
 */
const AddAwarenessStageForm = ({ data, onClose, onSubmit, isLoading, groups = [] }) => {
  const { userData } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);

  const [form, setForm] = useState({
    ...data,
    nome: data?.nome || "",
    descricao: data?.descricao || "",
    schwartz_level: data?.schwartz_level ?? "",
    funnel_phase: data?.funnel_phase || "",
    message_depth: data?.message_depth || "",
    ordem: data?.ordem ?? "",
    id_pai: data?.id_pai ?? "",
    excluido: data?.excluido == null ? 0 : Number(data.excluido),
    id_usuario: data?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
  });

  useEffect(() => {
    setForm((p) => ({
      ...p,
      ...data,
      nome: data?.nome || "",
      ordem: data?.ordem ?? "",
      id_pai: data?.id_pai ?? "",
      excluido: data?.excluido == null ? 0 : Number(data.excluido),
      id_usuario: data?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.id_pai]);

  const parents = useMemo(() => {
    const blocked = new Set([
      String(form.id || ""),
      ...collectAwarenessDescendantIds(groups, form.id).map(String),
    ]);
    return groups.filter((g) => !blocked.has(String(g.id)));
  }, [groups, form.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      const payload = {
        id: form.id,
        nome: (form.nome || "").trim(),
        descricao: (form.descricao || "").trim() || null,
        schwartz_level: form.schwartz_level === "" ? null : Number(form.schwartz_level),
        funnel_phase: form.funnel_phase || null,
        message_depth: form.message_depth || null,
        ordem: n(digitsOnly(form.ordem)),
        id_pai: form.id_pai || null,
        excluido: Number(form.excluido) ? 1 : 0,
        id_usuario: form.id_usuario || userData?.id,
        id_usuario_ultima_alteracao: userData?.id,
      };
      if (!payload.nome) throw new Error("Nome é obrigatório.");
      if (!payload.id) {
        const selected = getSelectedCompanyIds();
        const fallback = resolveSnapshotProjectId(form.id_projeto ?? null);
        const idsProjetos = selected.length ? selected : fallback ? [fallback] : [];
        if (idsProjetos.length) payload.ids_projetos = idsProjetos;
      }
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao salvar a etapa.");
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar Etapa" : "Adicionar Etapa"}
      </Typography>

      {errorMessage && <Alert sx={{ mb: 2 }} severity="error">{errorMessage}</Alert>}

      <form onSubmit={submit}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={7}>
            <TextField
              label="Nome"
              name="nome"
              value={form.nome}
              onChange={handleChange}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              name="descricao"
              value={form.descricao}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={2}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Nível Schwartz"
              name="schwartz_level"
              value={form.schwartz_level}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value=""><em>Custom / pós-venda</em></MenuItem>
              {[1, 2, 3, 4, 5].map((n) => (
                <MenuItem key={n} value={n}>{n}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Fase do funil"
              name="funnel_phase"
              value={form.funnel_phase}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value=""><em>—</em></MenuItem>
              {["topo", "meio", "fundo", "pos_venda", "retencao", "expansao"].map((p) => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Profundidade da mensagem"
              name="message_depth"
              value={form.message_depth}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value=""><em>—</em></MenuItem>
              {["raso", "medio", "profundo"].map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Ordem"
              name="ordem"
              value={form.ordem ?? ""}
              onChange={(e) => setForm((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))}
              inputMode="numeric"
              fullWidth
            />
          </Grid>

          {/* <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={Number(form.excluido) !== 1}
                  onChange={(e) => setForm((p) => ({ ...p, excluido: e.target.checked ? 0 : 1 }))}
                />
              }
              label={Number(form.excluido) !== 1 ? "Ativa" : "Inativa"}
            />
          </Grid> */}

          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Etapa Pai (opcional)"
              name="id_pai"
              value={form.id_pai ?? ""}
              onChange={handleChange}
              fullWidth
            >
              <MenuItem value="">— Nenhum (nível raiz)</MenuItem>
              {parents.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default AddAwarenessStageForm;
